import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { announcementsApi, eventsApi, sermonsApi, newsletterApi, settingsApi } from '@/lib/api';
import { Calendar, Clock, MapPin, Play, ChevronRight, Heart, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const HomePage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [latestSermon, setLatestSermon] = useState(null);
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [annData, eventsData, sermonsData, settingsData] = await Promise.all([
        announcementsApi.getAll().catch(() => []),
        eventsApi.getAll().catch(() => []),
        sermonsApi.getAll().catch(() => []),
        settingsApi.get().catch(() => null)
      ]);
      
      setAnnouncements(annData.slice(0, 3));
      setEvents(eventsData.slice(0, 4));
      setLatestSermon(sermonsData[0] || null);
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await newsletterApi.subscribe({ email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[600px] bg-gradient-to-r from-stone-900 to-stone-800 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1920')" }}
        />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
              Faith • Hope • Love
            </h1>
            <p className="text-xl md:text-2xl text-stone-200 mb-8 max-w-2xl mx-auto">
              Welcome to St. John's United Church of Christ. We are good people serving a GREAT GOD!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/about">
                <Button size="lg" className="bg-amber-700 hover:bg-amber-800 text-white text-lg px-8">
                  Learn More About Us
                </Button>
              </Link>
              <Link to="/sermons">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                  <Play className="mr-2 h-5 w-5" />
                  Watch Sermons
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Times Banner */}
      <section className="bg-amber-700 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-center items-center gap-2 md:gap-8">
          {Array.isArray(settings?.service_times) ? (
            settings.service_times.map((time, i) => (
              <div key={i} className="flex items-center">
                <Clock className="h-5 w-5 mr-2 shrink-0" />
                <span className="font-medium" data-testid={`homepage-service-time-${i}`}>{time}</span>
              </div>
            ))
          ) : (
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              <span className="font-medium" data-testid="homepage-service-times">{settings?.service_times || 'Sunday Worship: 10:00 AM'}</span>
            </div>
          )}
          <div className="flex items-center">
            <MapPin className="h-5 w-5 mr-2 shrink-0" />
            <span data-testid="homepage-address">{settings?.address || '1288 S. Indiana Ave, Crown Point, IN'}</span>
          </div>
        </div>
      </section>

      {/* Announcements Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-800 mb-4">
              Church Announcements
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Stay updated with the latest news and happenings at St. John's
            </p>
          </div>
          
          {announcements.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {announcements.map((announcement) => (
                <Card key={announcement.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="text-amber-700 text-sm font-medium mb-2">
                      {formatDate(announcement.date)}
                    </div>
                    <h3 className="text-xl font-semibold text-stone-800 mb-3">
                      {announcement.title}
                    </h3>
                    <p className="text-stone-600 line-clamp-3">
                      {announcement.content}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <p className="text-stone-600">Check back soon for announcements!</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Pastor's Message Section */}
      {settings?.pastor_message_content && (
        <section className="py-16 bg-stone-100">
          <div className="max-w-4xl mx-auto px-4">
            <Card className="overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-800 mb-6">
                  {settings.pastor_message_title || "A Message from Pastor Wendell"}
                </h2>
                <div className="prose prose-stone max-w-none">
                  <p className="text-stone-600 whitespace-pre-line">
                    {settings.pastor_message_content}
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-stone-200">
                  <p className="text-amber-700 font-medium">God's love and blessings!</p>
                  <p className="text-stone-600">Pastor Wendell and Kathy</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Latest Sermon */}
      {latestSermon && (
        <section className="py-16 bg-stone-800">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
                Latest Sermon
              </h2>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <Card className="overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/2 relative">
                    {latestSermon.thumbnail_data || latestSermon.thumbnail_url ? (
                      <img 
                        src={latestSermon.thumbnail_data || latestSermon.thumbnail_url} 
                        alt={latestSermon.title}
                        className="w-full h-64 md:h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-64 md:h-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center">
                        <Play className="h-16 w-16 text-white opacity-80" />
                      </div>
                    )}
                  </div>
                  <CardContent className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
                    <div className="text-amber-700 text-sm font-medium mb-2">
                      {formatDate(latestSermon.date)}
                    </div>
                    <h3 className="text-2xl font-semibold text-stone-800 mb-3">
                      {latestSermon.title}
                    </h3>
                    <p className="text-stone-500 mb-2">{latestSermon.speaker}</p>
                    {latestSermon.description && (
                      <p className="text-stone-600 mb-4 line-clamp-3">
                        {latestSermon.description}
                      </p>
                    )}
                    <Link to={`/sermons/${latestSermon.id}`}>
                      <Button className="bg-amber-700 hover:bg-amber-800">
                        <Play className="mr-2 h-4 w-4" />
                        Watch Now
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <Link to="/sermons">
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  View All Sermons
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-800 mb-4">
              Upcoming Events
            </h2>
          </div>
          
          {events.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {events.map((event) => (
                <Card key={event.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center text-amber-700 text-sm font-medium mb-3">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(event.date)}
                    </div>
                    <h3 className="text-lg font-semibold text-stone-800 mb-2">
                      {event.title}
                    </h3>
                    <p className="text-stone-600 text-sm mb-3 line-clamp-2">
                      {event.description}
                    </p>
                    <div className="flex items-center text-stone-500 text-sm">
                      <Clock className="h-4 w-4 mr-1" />
                      {formatTime(event.date)}
                    </div>
                    {event.location && (
                      <div className="flex items-center text-stone-500 text-sm mt-1">
                        <MapPin className="h-4 w-4 mr-1" />
                        {event.location}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <p className="text-stone-600">Check back soon for upcoming events!</p>
              </CardContent>
            </Card>
          )}
          
          <div className="text-center mt-8">
            <Link to="/events">
              <Button variant="outline">
                View All Events
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Prayer Request CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-700 to-amber-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Heart className="h-12 w-12 text-white mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Need Prayer?
          </h2>
          <p className="text-xl text-amber-100 mb-8">
            We believe in the power of prayer. Share your prayer requests with our church family.
          </p>
          <Link to="/prayer-requests">
            <Button size="lg" className="bg-white text-amber-800 hover:bg-stone-100">
              Submit Prayer Request
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-stone-100">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Mail className="h-12 w-12 text-amber-700 mx-auto mb-6" />
          <h2 className="text-3xl font-serif font-bold text-stone-800 mb-4">
            Stay Connected
          </h2>
          <p className="text-stone-600 mb-8">
            Subscribe to our newsletter to receive updates about church events and announcements.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1"
              required
              data-testid="newsletter-email-input"
            />
            <Button 
              type="submit" 
              className="bg-amber-700 hover:bg-amber-800"
              disabled={subscribing}
              data-testid="newsletter-subscribe-btn"
            >
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
